import { useEffect, useState, useCallback } from "react";
import useSWR from "swr";
import {
    Receipt, TrendUp, Warning, CheckCircle, ArrowsClockwise, Sparkle
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api, fmtEUR, fmtDate, fmtMonth } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SpendingChart from "@/components/SpendingChart";
import SupplierPieChart from "@/components/SupplierPieChart";
import SyncDialog from "@/components/SyncDialog";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Dashboard() {
    const { data: report, mutate: refetchReport } = useSWR("/reports/summary", fetcher);
    const { data: invoices, mutate: refetchInvoices } = useSWR("/invoices", fetcher);
    const { data: credentials } = useSWR("/amazon/credentials", fetcher);
    const { data: currentSync, mutate: refetchSync } = useSWR("/sync/current", fetcher, { refreshInterval: 2000 });

    const [syncOpen, setSyncOpen] = useState(false);

    const refreshAll = useCallback(() => {
        refetchReport();
        refetchInvoices();
        refetchSync();
    }, [refetchReport, refetchInvoices, refetchSync]);

    useEffect(() => {
        if (currentSync && ["success", "failed", "cancelled"].includes(currentSync.status)) {
            refreshAll();
        }
    }, [currentSync?.status, refreshAll]);

    const startSync = async () => {
        if (!credentials?.configured) {
            toast.error("Configurez d'abord vos identifiants Amazon dans Paramètres.");
            return;
        }
        try {
            await api.post("/sync/start", { months_back: 6, force: true });
            setSyncOpen(true);
            refetchSync();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors du lancement");
        }
    };

    const seedDemo = async () => {
        try {
            await api.post("/demo/seed");
            toast.success("Données de démo chargées");
            refreshAll();
        } catch (e) {
            toast.error("Impossible de charger la démo");
        }
    };

    const recentInvoices = (invoices || []).slice(0, 6);
    const missing = (invoices || []).filter((i) => !i.has_pdf);

    // Live sync banner — visible whenever ANY sync is currently active
    // (started from the dashboard, from the extension, or anywhere else).
    const syncActive = currentSync && ["pending", "running", "awaiting_otp"].includes(currentSync.status);
    const syncJustFinished = currentSync && ["success", "failed"].includes(currentSync.status)
        && currentSync.finished_at
        && (Date.now() - new Date(currentSync.finished_at).getTime()) < 60_000;

    return (
        <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto">
            {/* Live sync banner */}
            {(syncActive || syncJustFinished) && (
                <div
                    className={`mb-6 rounded-lg border px-4 py-3 flex items-center gap-3 ${
                        syncActive
                            ? "bg-blue-50 border-blue-200 text-blue-900"
                            : currentSync.status === "success"
                                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                                : "bg-red-50 border-red-200 text-red-900"
                    }`}
                    data-testid="sync-banner"
                >
                    {syncActive ? (
                        <ArrowsClockwise size={18} className="animate-spin shrink-0" />
                    ) : currentSync.status === "success" ? (
                        <CheckCircle size={18} weight="fill" className="shrink-0" />
                    ) : (
                        <Warning size={18} weight="fill" className="shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">
                            {syncActive ? "Synchronisation en cours" : currentSync.status === "success" ? "Synchronisation terminée" : "Synchronisation échouée"}
                        </div>
                        <div className="text-xs opacity-90 truncate">
                            {currentSync.message || "—"}
                        </div>
                        {syncActive && (
                            <div className="mt-2 h-1.5 w-full bg-white/60 rounded overflow-hidden">
                                <div
                                    className="h-full bg-blue-600 transition-all duration-300"
                                    style={{ width: `${Math.max(2, currentSync.progress || 0)}%` }}
                                />
                            </div>
                        )}
                    </div>
                    <div className="text-xs font-mono opacity-75 shrink-0">
                        {syncActive ? `${currentSync.progress || 0}%` : `${currentSync.invoices_found || 0} factures`}
                    </div>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSyncOpen(true)}
                        data-testid="sync-banner-details"
                    >
                        Détails
                    </Button>
                </div>
            )}

            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <p className="overline">Tableau de bord</p>
                    <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">
                        Vos factures
                    </h1>
                    <p className="text-muted-foreground text-sm mt-2 max-w-lg">
                        Synchronisation automatique multi-fournisseurs, détection des factures manquantes
                        et analyse des dépenses.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {(!invoices || invoices.length === 0) && (
                        <Button variant="outline" onClick={seedDemo} data-testid="seed-demo-btn">
                            <Sparkle size={16} className="mr-2" />
                            Charger une démo
                        </Button>
                    )}
                    <Button
                        asChild
                        className="bg-primary hover:bg-primary/90"
                        data-testid="goto-navigator-btn"
                    >
                        <Link to="/parametres">
                            <ArrowsClockwise size={16} className="mr-2" />
                            Lancer un import
                        </Link>
                    </Button>
                </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ec-stagger" data-testid="stats-grid">
                <StatCard
                    label="Total dépensé"
                    value={fmtEUR(report?.total_spent || 0)}
                    icon={TrendUp}
                    accent="text-primary"
                    testid="stat-total-spent"
                />
                <StatCard
                    label="Factures"
                    value={report?.total_invoices || 0}
                    icon={Receipt}
                    accent="text-secondary"
                    testid="stat-invoice-count"
                />
                <StatCard
                    label="Manquantes"
                    value={report?.missing_count || 0}
                    icon={Warning}
                    accent="text-amber-600"
                    testid="stat-missing"
                />
                <StatCard
                    label="Dernière sync"
                    value={credentials?.last_sync_at ? fmtDate(credentials.last_sync_at) : "Jamais"}
                    icon={CheckCircle}
                    accent="text-emerald-700"
                    testid="stat-last-sync"
                />
            </div>

            {/* Main grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Spending chart */}
                <Card className="lg:col-span-2 ec-card">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-1">
                            <h3 className="font-display text-lg font-bold tracking-tight">
                                Évolution des dépenses
                            </h3>
                            <Badge variant="outline" className="text-xs">
                                {report?.monthly?.length || 0} mois
                            </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">
                            Montant TTC cumulé par mois
                        </p>
                        <SpendingChart data={(report?.monthly || []).map((m) => ({ ...m, label: fmtMonth(m.period) }))} />
                    </CardContent>
                </Card>

                {/* Missing alert */}
                <Card className="ec-card border-amber-300 bg-amber-50/40" data-testid="missing-card">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Warning weight="fill" className="text-amber-600" size={20} />
                            <h3 className="font-display text-lg font-bold tracking-tight">
                                Factures manquantes
                            </h3>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">
                            Commandes détectées sans PDF, ou ruptures de numérotation.
                        </p>
                        {missing.length === 0 ? (
                            <div className="text-sm text-muted-foreground py-6 text-center">
                                Aucune facture manquante. ✓
                            </div>
                        ) : (
                            <ul className="space-y-2 max-h-72 overflow-auto">
                                {missing.slice(0, 8).map((inv) => (
                                    <li
                                        key={inv.id}
                                        className="text-sm flex items-start justify-between gap-2 p-2 rounded-md hover:bg-amber-100/40"
                                        data-testid={`missing-${inv.id}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="font-mono text-xs text-muted-foreground truncate">{inv.order_id}</div>
                                            <div className="text-xs">{fmtDate(inv.order_date)}</div>
                                        </div>
                                        <div className="text-sm font-semibold tabular text-amber-700 whitespace-nowrap">
                                            {fmtEUR(inv.total_ttc, inv.currency)}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>

                {/* Supplier breakdown pie chart */}
                <div className="lg:col-span-3">
                    <SupplierPieChart invoices={invoices} />
                </div>

                {/* Recent invoices */}
                <Card className="lg:col-span-3 ec-card">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="font-display text-lg font-bold tracking-tight">
                                    Factures récentes
                                </h3>
                                <p className="text-xs text-muted-foreground">Les 6 dernières détectées</p>
                            </div>
                            <Link to="/factures">
                                <Button variant="outline" size="sm" data-testid="view-all-invoices">
                                    Tout voir
                                </Button>
                            </Link>
                        </div>
                        {recentInvoices.length === 0 ? (
                            <EmptyState onSeed={seedDemo} onSync={startSync} hasCredentials={credentials?.configured} />
                        ) : (
                            <div className="divide-y divide-border">
                                {recentInvoices.map((inv) => (
                                    <div
                                        key={inv.id}
                                        className="flex items-center justify-between py-3"
                                        data-testid={`recent-invoice-${inv.id}`}
                                    >
                                        <div className="min-w-0 flex items-center gap-3">
                                            <div className="h-9 w-9 rounded-md bg-accent flex items-center justify-center shrink-0">
                                                <Receipt size={16} className="text-primary" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-semibold truncate">
                                                    {inv.invoice_number || <span className="text-muted-foreground">Sans n° de facture</span>}
                                                </div>
                                                <div className="text-xs text-muted-foreground font-mono truncate">{inv.order_id}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-6">
                                            <div className="text-xs text-muted-foreground hidden sm:block">{fmtDate(inv.order_date)}</div>
                                            <Badge
                                                variant="outline"
                                                className={
                                                    inv.has_pdf
                                                        ? "text-emerald-700 border-emerald-200"
                                                        : "text-amber-700 border-amber-200"
                                                }
                                            >
                                                {inv.has_pdf ? "Complet" : "PDF manquant"}
                                            </Badge>
                                            <div className="text-sm font-display font-bold tabular w-20 text-right">
                                                {fmtEUR(inv.total_ttc, inv.currency)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <SyncDialog
                open={syncOpen}
                onOpenChange={(v) => { setSyncOpen(v); if (!v) refreshAll(); }}
                currentSync={currentSync}
                onRefresh={refetchSync}
            />
        </div>
    );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
    return (
        <Card className="ec-card ec-card-hover" data-testid={testid}>
            <CardContent className="p-5">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="overline">{label}</p>
                        <p className="font-display text-2xl lg:text-3xl font-bold mt-2 tabular tracking-tight">{value}</p>
                    </div>
                    <Icon size={22} className={accent} weight="duotone" />
                </div>
            </CardContent>
        </Card>
    );
}

function EmptyState({ onSeed, hasCredentials }) {
    return (
        <div className="text-center py-12 px-6 border border-dashed border-border rounded-md">
            <div className="inline-flex h-12 w-12 rounded-full bg-accent items-center justify-center mb-4">
                <Receipt size={22} className="text-primary" />
            </div>
            <h4 className="font-display text-lg font-bold mb-2">Aucune facture pour le moment</h4>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-5">
                {hasCredentials
                    ? "Va dans Paramètres → Amazon Mode cookies pour lancer un import."
                    : "Configure tes connecteurs dans Paramètres puis lance un import, ou explore l'app avec des données de démo."}
            </p>
            <div className="flex items-center justify-center gap-2">
                <Button asChild className="bg-primary hover:bg-primary/90">
                    <Link to="/parametres">
                        <ArrowsClockwise size={16} className="mr-2" />
                        Lancer un import
                    </Link>
                </Button>
                <Button variant="outline" onClick={onSeed}>
                    <Sparkle size={16} className="mr-2" />
                    Démo
                </Button>
            </div>
        </div>
    );
}
